import type {
  BaseNetworkHandler,
  Config,
  Dimensions,
  Graphene,
  InitializeProps,
} from '@snapchat/graphene';
import { graphene, PARTITION } from '@snapchat/graphene';
import type { WebMetricCollectorOptions } from '@snapchat/graphene';
import { getWebConfig } from '@snapchat/graphene';
import isNil from 'lodash/isNil.js';

import type { BaseEventListenerProps } from './AbstractEventListener';
import { AbstractEventListener } from './AbstractEventListener';
import type { BasePermission, EmptyObject, FlatContext, LoggedEvent } from './loggingTypes';
import { LoggingEventType } from './loggingTypes';

/**
 * The limit of how many dimensions graphene supports. Per event graphene supports 6 slices one
 * going to the `metricsName`.
 */
const maxDimensions = 5;

export interface GrapheneEvent {
  metricsName: string;
  /** Value for addHistogram call to Graphene. */
  value?: number;
  /** Value for addTiming call to Graphene */
  timing?: number;
  dimensions?: Record<string, unknown>;
}

export type GrapheneLoggingContext = {
  /** If there are no values or timings, this will pass this into the addCounter call of graphene. */
  incrementValue: number;
};

type Props<
  LoggingContext extends GrapheneLoggingContext = GrapheneLoggingContext,
  CustomEvents extends EmptyObject = EmptyObject
> = Omit<InitializeProps, 'networkHandler'> &
  WebMetricCollectorOptions &
  BaseEventListenerProps<LoggingContext, CustomEvents> & {
    /**
     * Function that extracts additional the dimensions from context to add to the default ones.
     *
     * Note that graphene can only support 5, so the first non-null 5 will be recorded. The first
     * few usually come from the event, and not the context, so it's not unlikely that most of the
     * fields here will not be recorded in all use-cases.
     */
    getAdditionalDimensions?: (context: Partial<LoggingContext>) => FlatContext | undefined;

    /** Returns the graphene metric name. If not specifies a sensible default is used. */
    getMetricName?: (
      event: LoggedEvent<CustomEvents>,
      context: Partial<LoggingContext>
    ) => string | null;

    /**
     * Processor for custom events.
     *
     * @returns {undefined} MetricsName, value?, Dimensions or undefined if event not processed.
     */
    customEventFormat?: (
      event: CustomEvents,
      context: Partial<LoggingContext>
    ) => GrapheneEvent | null | undefined;
  };

/**
 * Abstract Graphene EventListener that implements formatting but not the delivery. If no
 * partitionName is passed, it will log to PARTITION.WEB_COMMON.
 */
export abstract class AbstractGrapheneEventListener<
  LoggingContext extends GrapheneLoggingContext = GrapheneLoggingContext,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends BasePermission = BasePermission
> extends AbstractEventListener<LoggingContext, CustomEvents, LoggingPermission> {
  private readonly props: Props<LoggingContext, CustomEvents>;
  // TODO: Consider making this strict. I.e. /\W+/g, but that would convert
  // characters like ':' into underscores. But I'm not sure if that's better or
  // worse. Right now, some characters get turned into ':'. So https://localhost/foo
  // can either be https___localhost_foo or https:::localhost:foo and it's unclear what's
  // better.
  private readonly illegalCharRegex = /[\s.]+/g;

  private client: Graphene = graphene;

  constructor(
    props: Omit<Props<LoggingContext, CustomEvents>, 'partitionName'> & {
      partitionName?: PARTITION;
    }
  ) {
    super({
      ...props,
      requiredPermissions: ['logging'],
    });

    this.props = {
      ...props,
      partitionName: props.partitionName ?? PARTITION.WEB_COMMON,
    };
  }

  /** Returns the network handler for sending Graphene events. */
  protected abstract getNetworkHandler: (options: Config) => BaseNetworkHandler;

  /** Returns the client for graphene. */
  protected abstract getClient: () => Graphene;

  /** @override */
  protected init = (): Promise<void> => {
    // getClient creates a new client so it is important that we only call it once.
    this.client = this.getClient();

    // Sanity check that this isn't an already initialized client.
    if (this.client.isInitialized) {
      console.warn('Graphene client already initialized. Ignoring init call.');
      return Promise.resolve();
    }

    // We sanitize the props because graphene silently drops events if they have
    // special characters in flavor or variant.
    const grapheneConfigs = {
      ...this.props,
      flavor: this.props.flavor ? this.cleanDimensionString(this.props.flavor) : undefined,
      variant: this.props.variant ? this.cleanDimensionString(this.props.variant) : undefined,
    };

    this.client.initialize({
      networkHandler: this.getNetworkHandler(getWebConfig(grapheneConfigs)),
      ...grapheneConfigs,
    });

    return Promise.resolve();
  };

  /** @override */
  protected logEventInternal(
    event: LoggedEvent<CustomEvents>,
    context: Partial<LoggingContext>
  ): void {
    const metricsName = this.props.getMetricName?.(event, context);

    switch (event.type) {
      case LoggingEventType.DEBUG: {
        // Log nothing for debug events. They are too noisy for graphene.
        break;
      }

      case LoggingEventType.INFO: {
        const dimensions = this.getDimensions(
          {
            action: event.action,
            category: event.component,
            label: event.label,
          },
          context
        );

        this.client.increment({
          metricsName: metricsName ?? 'app_event',
          dimensions,
          value: context.incrementValue ?? 1,
        });
        break;
      }

      case LoggingEventType.TIMING: {
        const dimensions = this.getDimensions(
          {
            variable: event.variable,
            category: event.component,
            label: event.label,
          },
          context
        );

        this.client.addTimer({
          metricsName: metricsName ?? 'app_timing_event',
          dimensions,
          milliSec: Math.round(event.valueMs),
        });
        break;
      }

      case LoggingEventType.VALUE: {
        const dimensions = this.getDimensions(
          {
            variable: event.variable,
            category: event.component,
            label: event.label,
          },
          context
        );

        this.client.addHistogram({
          metricsName: metricsName ?? 'app_measure_event',
          dimensions,
          value: event.value,
        });
        break;
      }

      case LoggingEventType.WARNING: {
        const dimensions = this.getDimensions({ category: event.component }, context);

        this.client.increment({
          metricsName: metricsName ?? 'app_warning',
          dimensions,
        });
        break;
      }

      case LoggingEventType.ERROR: {
        const dimensions = this.getDimensions(
          { category: event.component, action: event.action },
          context
        );

        this.client.increment({
          metricsName: metricsName ?? 'app_error',
          dimensions,
        });
        break;
      }

      case LoggingEventType.USER_ACTION: {
        const dimensions = this.getDimensions(
          { category: event.component, action: event.action, label: event.label },
          context
        );

        this.client.increment({
          metricsName: metricsName ?? 'user_action',
          dimensions,
        });
        break;
      }

      case LoggingEventType.CUSTOM: {
        const customEvent = this.props.customEventFormat?.(event, context);
        if (!customEvent) break;
        const { metricsName, value, timing, dimensions: extractedDimensions } = customEvent;
        const dimensions = this.getDimensions(extractedDimensions ?? {}, context);

        if (value) {
          this.client.addHistogram({ metricsName, dimensions, value });
        } else if (timing) {
          // NOTE: Timing has to be a whole number.
          this.client.addTimer({ metricsName, dimensions, milliSec: Math.round(timing) });
        } else {
          this.client.increment({ metricsName, dimensions, value: context.incrementValue ?? 1 });
        }
        break;
      }
    }
  }

  /** Aggregates up to {@link maxDimensions} from the event and context. */
  protected getDimensions = (
    eventDimensions: Record<string, unknown>,
    context: Partial<LoggingContext>
  ): Dimensions => {
    const dimensions: Dimensions = {};

    // Copy provided dimensions from the event
    for (const [key, value] of Object.entries(eventDimensions)) {
      if (isNil(value)) continue;
      const dimensionKey = this.cleanDimensionString(key);
      dimensions[dimensionKey] = this.cleanDimensionString(String(value));
    }

    // Copies the dimensions from context.
    if (this.props.getAdditionalDimensions) {
      const flatContext = this.props.getAdditionalDimensions(context) ?? {};

      for (const [key, value] of Object.entries(flatContext)) {
        if (isNil(value)) continue;
        if (Object.keys(dimensions).length >= maxDimensions) break;
        const dimensionKey = this.cleanDimensionString(String(key));
        dimensions[dimensionKey] = this.cleanDimensionString(String(value));
      }
    }

    return dimensions;
  };

  /**
   * Graphene doesn't support empty strings, so replace with 'Unknown'. Also replace any whitespace
   * or periods in the input string with underscores, this avoids issues finding the logs later due
   * to the partitions not matching expectations. Example: convert '523.snap.com' to '523_snap_com'
   * since periods represent partitions in Graphene.
   */
  protected cleanDimensionString = (input?: string): string => {
    if (!input) return 'Unknown';
    return input.replace(this.illegalCharRegex, '_');
  };

  /** @override */
  protected flushInternal = async (): Promise<void> => {
    await this.client?.flush();
  };
}
